import { useEffect, useMemo, useRef, useState } from 'react';
import { useBahasa } from '../i18n';

/**
 * Pemilih orang: **ketik namanya, bukan cari di antara 46 kotak centang**
 * (§C2.2).
 *
 * Bentuk sebelumnya menurunkan seluruh daftar user sebagai kotak centang di
 * dalam formulir. Pada 46 akun itu enam belas baris yang selalu ada, selalu
 * penuh, dan hampir seluruhnya tidak relevan: satu dashboard biasanya
 * ditugaskan ke dua sampai lima orang. Yang dibayar bukan cuma ruang layar —
 * tombol Save terdorong jauh ke bawah lipatan, dan tombol yang tidak terlihat
 * sama saja dengan tombol yang tidak ada.
 *
 * Yang menggantikannya dua hal yang bekerja bersama:
 *
 * - **Chip** untuk yang sudah dipilih. Ia satu-satunya yang perlu terlihat
 *   terus-menerus, dan jumlahnya memang sedikit.
 * - **Modal berisi kotak ketik** untuk menambah. Mengetik dua huruf lebih cepat
 *   daripada memindai 46 nama, dan yang tidak hafal namanya tetap bisa
 *   menggulung daftar penuh di dalam modal yang sama — jadi tidak ada jalan
 *   yang tertutup, hanya satu jalan yang jadi jauh lebih pendek.
 *
 * Daftarnya disaring **di klien**: 46 baris sudah ada seluruhnya di memori
 * halaman ini sejak dimuat, dan meminta server menyaringnya lagi cuma
 * menambahkan satu perjalanan jaringan di antara ketikan orang dan jawabannya.
 */

export interface OrangPilihan {
  id: string;
  /** Yang dicari orang saat mengetik. */
  username: string;
  fullName: string;
  /** Mis. `(owner)` — keterangan kecil di sebelah namanya. */
  catatan?: string;
}

function cocok(o: OrangPilihan, q: string): boolean {
  if (q === '') return true;
  const k = q.toLowerCase();
  return o.username.toLowerCase().includes(k) || o.fullName.toLowerCase().includes(k);
}

export function PilihOrang({
  legend,
  hint,
  semua,
  terpilih,
  onChange,
  empty,
  kosong,
  tambah,
}: {
  legend: string;
  hint?: string;
  semua: OrangPilihan[];
  terpilih: string[];
  onChange: (ids: string[]) => void;
  empty: string;
  /** Keterangan saat belum ada yang dipilih. Bawaan "Nobody yet.". */
  kosong?: string;
  /** Label tombol tambah. Bawaan "Add people…". */
  tambah?: string;
}) {
  const { t } = useBahasa();
  const [buka, setBuka] = useState(false);

  const byId = useMemo(() => new Map(semua.map((o) => [o.id, o])), [semua]);

  return (
    <fieldset className="mt-4">
      <legend className="text-sm font-medium">{legend}</legend>
      {hint && <p className="mt-0.5 text-xs text-muted">{hint}</p>}

      {semua.length === 0 ? (
        <p className="mt-2 text-sm text-muted">{empty}</p>
      ) : (
        <>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {terpilih.length === 0 && (
              <span className="text-sm text-muted">{kosong ?? t('Nobody yet.')}</span>
            )}
            {terpilih.map((id) => {
              const o = byId.get(id);
              return (
                <span
                  key={id}
                  className="inline-flex items-center gap-1 rounded-full border border-line bg-subtle py-0.5 pl-2.5 pr-1 text-sm"
                >
                  {o?.username ?? id}
                  {o?.catatan && <span className="text-xs text-muted">{o.catatan}</span>}
                  <button
                    type="button"
                    onClick={() => onChange(terpilih.filter((x) => x !== id))}
                    // Nama ikut ke dalam label: sepuluh tombol "Remove" yang
                    // dibacakan berurutan tidak memberi tahu satu pun dari
                    // sepuluh nama yang sedang dilepas.
                    aria-label={t('Remove {nama}', { nama: o?.username ?? id })}
                    className="flex h-6 w-6 items-center justify-center rounded-full text-muted hover:bg-line hover:text-content"
                  >
                    ×
                  </button>
                </span>
              );
            })}

            <button
              type="button"
              onClick={() => setBuka(true)}
              className="inline-flex min-h-tap items-center rounded-md border border-line px-3 text-sm hover:bg-subtle"
            >
              {tambah ?? t('Add people…')}
            </button>
          </div>

          {buka && (
            <ModalPilihOrang
              judul={legend}
              semua={semua}
              terpilih={terpilih}
              onChange={onChange}
              onTutup={() => setBuka(false)}
            />
          )}
        </>
      )}
    </fieldset>
  );
}

/**
 * Modalnya sendiri.
 *
 * **Pilihan langsung berlaku, tidak ada tombol OK.** Chip di formulir berubah
 * sementara modalnya masih terbuka, dan itu disengaja: yang sedang dikerjakan
 * orang adalah menyusun daftar, dan daftar yang tumbuh di belakang dialog yang
 * harus ditutup dulu untuk dilihat bukan umpan balik, ia tebakan. Membatalkan
 * tetap ada — lepas chip-nya, satu klik di tempat yang sama.
 */
function ModalPilihOrang({
  judul,
  semua,
  terpilih,
  onChange,
  onTutup,
}: {
  judul: string;
  semua: OrangPilihan[];
  terpilih: string[];
  onChange: (ids: string[]) => void;
  onTutup: () => void;
}) {
  const { t } = useBahasa();
  const [q, setQ] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      onTutup();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onTutup]);

  const hasil = useMemo(() => semua.filter((o) => cocok(o, q.trim())), [semua, q]);

  function toggle(id: string) {
    onChange(terpilih.includes(id) ? terpilih.filter((x) => x !== id) : [...terpilih, id]);
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={judul}
    >
      <div className="flex max-h-[80vh] w-full max-w-md flex-col rounded-lg border border-line bg-surface p-4 shadow-xl">
        <div className="flex items-center gap-2">
          <h2 className="mr-auto text-base">{judul}</h2>
          <button
            type="button"
            onClick={onTutup}
            aria-label={t('Close')}
            className="flex h-9 w-9 items-center justify-center rounded-md hover:bg-subtle"
          >
            ×
          </button>
        </div>

        <input
          ref={inputRef}
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('Type a name or username')}
          aria-label={t('Type a name or username')}
          // Enter di sini tidak boleh menyimpan formulir yang ada di belakang
          // modal ini: kotak ini duduk **di dalam** `<form>` halaman Camera
          // Access, dan satu kotak teks di dalam form membuat Enter memicu
          // pengiriman implisit. Orang yang mengetik nama lalu menekan Enter
          // akan menyimpan dashboard yang belum selesai disusunnya.
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.preventDefault();
          }}
          className="mt-3 min-h-tap w-full rounded-md border border-line bg-page px-3 text-base"
        />

        <p className="mt-2 text-xs text-muted" aria-live="polite">
          {t('{n} selected', { n: terpilih.length })}
          {q.trim() !== '' && ` · ${t('{n} match', { n: hasil.length })}`}
        </p>

        <ul className="mt-2 min-h-0 flex-1 overflow-y-auto rounded-md border border-line">
          {hasil.length === 0 ? (
            <li className="p-3 text-sm text-muted">
              {t('Nobody matches “{cari}”.', { cari: q.trim() })}
            </li>
          ) : (
            hasil.map((o) => (
              <li key={o.id}>
                <label className="flex min-h-tap cursor-pointer items-center gap-2.5 border-b border-line px-3 text-sm last:border-b-0 hover:bg-subtle">
                  <input
                    type="checkbox"
                    checked={terpilih.includes(o.id)}
                    onChange={() => toggle(o.id)}
                    className="h-4 w-4 shrink-0 accent-[var(--color-accent-solid)]"
                  />
                  <span className="min-w-0 flex-1 truncate">
                    {o.username}
                    <span className="ml-1.5 text-xs text-muted">{o.fullName}</span>
                  </span>
                  {o.catatan && <span className="shrink-0 text-xs text-muted">{o.catatan}</span>}
                </label>
              </li>
            ))
          )}
        </ul>

        <div className="mt-3 flex justify-end">
          <button
            type="button"
            onClick={onTutup}
            className="min-h-tap rounded-md bg-accent px-4 text-sm text-accent-contrast"
          >
            {t('Done')}
          </button>
        </div>
      </div>
    </div>
  );
}
